<template>
  <div class="flex flex-col">
    <VcAlert
      v-if="showItemAddedNotification"
      color="success"
      size="sm"
      variant="outline-dark"
      class="mb-6 gap-2"
      icon
      closable
      @close="closeItemAddedAlert"
    >
      {{ itemAddedNotification }}
    </VcAlert>

    <VcAlert
      v-if="showItemRemovedNotification"
      color="success"
      size="sm"
      variant="outline-dark"
      class="mb-6 gap-2"
      icon
      closable
      @close="closeItemRemovedAlert"
    >
      {{ itemRemovedNotification }}
    </VcAlert>

    <div class="header__top flex-auto">
      <!-- Headers -->
      <div v-if="!isMobile" class="mb-5 flex border-b pb-3">
        <span class="flex text-base font-semibold md:flex-[0_0_55%] lg:flex-[0_0_64%]">
          {{ $t("common.labels.minicart_item") }}
        </span>
        <span class="header__top-qty flex flex-[0_0_14%] justify-center text-base font-semibold">
          {{ $t("common.labels.minicart_quantity") }}
        </span>
        <span class="flex flex-1 justify-end text-base font-semibold">
          {{
            EXCL_VAT_CURRENCIES.includes(currentCurrency.code)
              ? $t("shared.cart.add_bulk_items_to_cart_results_modal.labels.amount_excl_vat")
              : $t("shared.cart.add_bulk_items_to_cart_results_modal.labels.amount")
          }}
        </span>
      </div>
      <!--Iterate cart items-->
      <div
        v-for="item in availabilityItems"
        :key="item.lineItem.id"
        class="cart__item mb-5 flex flex-auto flex-col border-b pb-5 last-of-type:mb-0 md:pb-5"
      >
        <div :class="{ 'flex flex-auto gap-4': true, disabled: isDisabled }" :style="{ opacity: isDisabled ? 0.5 : 1 }">
          <div class="cart__item-image flex size-[80px] items-center md:size-[100px]">
            <VcImage
              :src="
                item.lineItem.imageUrl
                  ? item.lineItem.imageUrl
                  : item.lineItem.product?.masterVariation && item.lineItem.product?.masterVariation.images
                    ? item.lineItem.product?.masterVariation.images[0].url
                    : undefined
              "
              alt=""
              multiple-dpr
              :use-original-src="lineItemIsCustomPatchCable(item.lineItem)"
              :default-size="DEFAULT_DETAIL_THUMB_SIZE"
              :width="DEFAULT_THUMB_SIZE"
              :height="DEFAULT_THUMB_SIZE"
            ></VcImage>
          </div>
          <div class="flex w-full flex-col md:flex-row md:gap-8">
            <div class="cart__item-details flex flex-1 flex-col">
              <a
                class="line-clamp-2 cursor-pointer items-start gap-1.5 overflow-hidden text-ellipsis font-semibold text-[--color-link] hover:text-[--color-neutral-a1]"
                :href="getProductFamilyPageUrl(item.lineItem)"
                @click="handleButtonClick()"
              >
                {{ item.productTitle }}
              </a>
              <div v-dompurify-html="getItemDescription(item.lineItem)" class="text-base font-semibold leading-6"></div>
              <div v-if="isMobile" class="mb-0 mt-3 flex items-start justify-between gap-1 md:my-0 md:mb-1">
                <div v-if="item.lineItem.listPrice && isMobile" class="flex">
                  <span class="text-base font-normal md:text-sm">
                    {{ $t("shared.catalog.product_details.product_variations.availability") }}: {{ item.status }}
                  </span>
                </div>
                <div v-if="isMobile" class="cart__item-price flex flex-col items-end gap-1 md:gap-0">
                  <span
                    :class="{
                      'price whitespace-nowrap text-base font-bold': true, // Base styles
                      'price-line-trough font-normal text-[--color-neutral-a3] line-through':
                        item.lineItem.placedPrice.amount > 0 &&
                        item.lineItem.listPrice?.amount > item.lineItem.placedPrice.amount, // Apply line-through if there's a discount
                    }"
                  >
                    <VcItemPrice
                      :value="{
                        list: getProductPrice(item.lineItem, true),
                        actual: getProductPrice(item.lineItem, true),
                      }"
                      :list-with-tax="item.lineItem.listPriceWithTaxAdaptedToQuantityTL"
                      :actual-with-tax="item.lineItem.listPriceWithTaxAdaptedToQuantityTL"
                      @request-price-clicked="showPriceRequestDialog(item.lineItem.product)"
                    />
                  </span>
                  <div
                    v-if="
                      getProductPrice(item.lineItem, true).amount > 0 &&
                      item.lineItem.placedPrice.amount > 0 &&
                      item.lineItem.listPrice?.amount > item.lineItem.placedPrice.amount
                    "
                  >
                    <span class="price-promo text-base font-bold">
                      <VcItemPrice
                        :value="{
                          list: item.lineItem.listPriceAdaptedToQuantityTL,
                          actual: item.lineItem.extendedPrice,
                        }"
                        :list-with-tax="item.lineItem.listPriceWithTaxAdaptedToQuantityTL"
                        :actual-with-tax="item.lineItem.extendedPriceWithTax"
                        @request-price-clicked="showPriceRequestDialog(item.lineItem.product)"
                      />
                    </span>
                  </div>
                </div>
              </div>
              <div v-if="item.lineItem.product?.mainProductId" class="text-base md:mt-1 md:text-sm">
                <span> {{ $t("shared.cart.serial") }} #: {{ item.lineItem.sku }} </span>
              </div>

              <div
                v-if="item.lineItem.product?.isCalibrationServiceTL"
                class="cart__serial-number mt-2 text-sm leading-4 md:mt-1"
              >
                <span v-if="item.lineItem.itemNumberTL && item.lineItem.itemNumberTL.length > 0">
                  {{ $t("shared.cart.item") }} #: {{ item.lineItem.itemNumberTL }}
                </span>
                <span
                  v-if="
                    item.lineItem.serialNumberTL &&
                    item.lineItem.serialNumberTL.length > 0 &&
                    item.lineItem.itemNumberTL &&
                    item.lineItem.itemNumberTL.length > 0
                  "
                  class="mx-2 text-[--color-neutral-a3]"
                  >|</span
                >
                <span v-if="item.lineItem.serialNumberTL && item.lineItem.serialNumberTL.length > 0">
                  {{ $t("shared.cart.serial") }} #: {{ item.lineItem.serialNumberTL }}
                </span>
              </div>

              <div v-if="shouldShowComments(item)" class="cart__serial-number mt-2 text-sm leading-4 md:mt-1">
                <span>
                  {{ $t("shared.cart.comments") }}:
                  {{ item.lineItem.configurationItemsTL?.find((c) => c.customText && c.customText !== "")?.customText }}
                </span>
              </div>

              <div v-if="!isMobile" class="mt-2 flex gap-4">
                <!-- Remove button -->
                <button
                  :aria-label="$t('shared.cart.remove_from_cart')"
                  type="button"
                  class="flex items-center text-sm font-normal hover:underline"
                  @click="removeCartItem(item.lineItem.id)"
                >
                  <VcIcon name="trash-2" :size="20" class="mt-1" />
                  <span> {{ $t("shared.cart.remove_from_cart") }}</span>
                </button>
              </div>
            </div>
            <div
              class="cart__item-quantity mt-4 flex items-center justify-between md:mt-0 md:items-start md:justify-normal"
            >
              <div v-if="isMobile" class="flex gap-8">
                <!-- Remove button -->
                <button
                  :aria-label="$t('shared.cart.remove_from_cart')"
                  type="button"
                  class="flex items-center text-sm font-normal hover:underline"
                  @click="removeCartItem(item.lineItem.id)"
                >
                  <VcIcon name="trash-2" :size="20" class="mt-1" />
                  <span v-if="!isMobile"> {{ $t("shared.cart.remove_from_cart") }}</span>
                </button>
              </div>

              <VcQuantity
                :model-value="lineItemQtyMap?.get(item.lineItem.id) ?? item.lineItem.quantity"
                :disabled="isQtyDisabled(item.lineItem)"
                :name="item.lineItem.id"
                :class="['quantity-box', { 'cursor-not-allowed': isQtyDisabled(item.lineItem) }]"
                @update:model-value="changeItemQuantity(item.lineItem.id, $event)"
              >
                <template #prepend>
                  <VcButton
                    :aria-label="$t('common.buttons.decrease_quantity')"
                    :disabled="isQtyDisabled(item.lineItem)"
                    icon="minus"
                    color="tertiary"
                    variant="outline"
                    :class="['qty-left w-[60px] md:w-[44px]', { 'cursor-not-allowed': isQtyDisabled(item.lineItem) }]"
                    size="xs"
                    @keyup.enter="changeItemQuantity(item.lineItem.id, $event)"
                    @click="decreaseQuantity(item.lineItem)"
                  />
                </template>
                <template #append>
                  <VcButton
                    :aria-label="$t('common.buttons.increase_quantity')"
                    :disabled="isQtyDisabled(item.lineItem)"
                    icon="plus"
                    color="tertiary"
                    variant="outline"
                    :class="['qty-right w-[60px] md:w-[44px]', { 'cursor-not-allowed': isQtyDisabled(item.lineItem) }]"
                    size="xs"
                    @keyup.enter="changeItemQuantity(item.lineItem.id, $event)"
                    @click="increaseQuantity(item.lineItem)"
                  />
                </template>
              </VcQuantity>
            </div>
            <div
              v-if="item.lineItem.listPrice && !isMobile"
              class="cart__item-price flex flex-col items-end md:w-[145px]"
            >
              <!-- Promo price logic, displayed only if discountTotal exists and is greater than or equal to 0 -->
              <div
                v-if="
                  getProductPrice(item.lineItem, true).amount > 0 &&
                  ((item.lineItem.placedPrice.amount > 0 &&
                    item.lineItem.listPrice?.amount > item.lineItem.placedPrice.amount) ||
                    !item.lineItem.listPrice.amount)
                "
              >
                <span class="price-promo text-base font-bold">
                  <VcItemPrice
                    :value="{ list: item.lineItem.listPriceAdaptedToQuantityTL, actual: item.lineItem.extendedPrice }"
                    :list-with-tax="item.lineItem.listPriceWithTaxAdaptedToQuantityTL"
                    :actual-with-tax="item.lineItem.extendedPriceWithTax"
                    @request-price-clicked="showPriceRequestDialog(item.lineItem.product)"
                  />
                </span>
              </div>

              <!-- Original price logic -->
              <span
                v-if="!item.lineItem.discounts?.find((d) => d.description === 'VOLUME_CUSTOM')"
                :class="{
                  'price whitespace-nowrap text-base font-bold': true, // Base styles
                  'price-line-trough font-normal text-[--color-neutral-a2] line-through':
                    item.lineItem.placedPrice.amount > 0 &&
                    item.lineItem.listPrice?.amount > item.lineItem.placedPrice.amount, // Apply line-through if there's a discount
                }"
              >
                <VcItemPrice
                  :value="{
                    list: getProductPrice(item.lineItem, true),
                    actual: getProductPrice(item.lineItem, true),
                  }"
                  :list-with-tax="item.lineItem.listPriceWithTaxAdaptedToQuantityTL"
                  :actual-with-tax="item.lineItem.listPriceWithTaxAdaptedToQuantityTL"
                  @request-price-clicked="showPriceRequestDialog(item.lineItem.product)"
                />
              </span>
              <span class="mt-3 text-right text-sm font-normal leading-6">
                {{ $t("shared.catalog.product_details.product_variations.availability") }}: {{ item.status }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="sticky bottom-0 z-10 mx-[-2px] bg-[--color-white] pb-[25px] pt-5 md:mx-0">
    <div v-if="!isMobile" class="flex justify-end gap-1.5 font-bold">
      {{ getSubtotalLabel() }} {{ itemsInCart }}:
      <VcPriceDisplay :formatted-value="formatPrice(cartTotalForCartPage?.amount || 0, currentCurrency)" />
    </div>
    <div v-if="isMobile" class="flex justify-start gap-1.5 font-bold">
      <div
        :class="['flex', `${currentCurrency.code === 'CNY' ? 'flex-col' : ''}`, 'font-bold', 'items-end', 'gap-1.5']"
      >
        <span class="flex">
          {{ getSubtotalLabel() }}
          <span v-if="currentCurrency.code === 'CNY'">:</span>
        </span>
        <span class="flex">
          {{ itemsInCart }}
          <span v-if="currentCurrency.code !== 'CNY'">:</span>
        </span>
      </div>
      <VcPriceDisplay :formatted-value="formatPrice(cartTotalForCartPage?.amount || 0, currentCurrency)" />
    </div>

    <div class="mt-6 md:mt-14">
      <VcButton
        v-bind="$attrs"
        type="button"
        class="flex w-full"
        color="primary"
        variant="solid"
        size="lg"
        @click="handleCheckoutClick"
      >
        <template v-if="punchoutCookies">
          {{ $t("common.labels.view_punchout_cart") }}
        </template>
        <template v-else>
          {{ $t("common.labels.view_cart_checkout") }}
        </template>
      </VcButton>

      <VcButton
        v-if="availabilityItems && availabilityItems.length > 0"
        color="secondary"
        variant="outlineTL"
        size="lg"
        class="mt-4 flex w-full items-center"
        :loading="forwardCartLoading"
        @click="handleForwardCartClick"
      >
        {{ $t("pages.cart.forward_cart") }}
      </VcButton>

      <VcButton
        color="secondary"
        variant="outlineTL"
        size="lg"
        class="mt-4 flex w-full items-center"
        @click="handleButtonClick"
      >
        {{ $t("common.buttons.continue_shopping") }}
      </VcButton>
    </div>
  </div>

  <ForwardCart ref="forwardCartRef" />
</template>

<script lang="ts" setup>
import { breakpointsTailwind, useBreakpoints } from "@vueuse/core";
import { ref, defineEmits, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { useCurrency } from "@/core/composables";
import { EXCL_VAT_CURRENCIES } from "@/core/constants";
import { punchoutCookies } from "@/core/utilities/punchout/punchoutCookies";
import { useUser } from "@/shared/account";
import { getProductFamilyPageUrl, getProductPrice, lineItemIsCustomPatchCable } from "@/shared/cart/utils";
import { formatPrice } from "@/shared/cart/utils/formatPrice";
import { showPriceRequestDialog } from "@/shared/catalog/utils";
import { useFullCart } from "../composables/useCart";
import type {
  CartConfigurationItemTypeTl,
  InputCustomerReferenceType,
  LineItemTypeExtension,
} from "@/core/api/graphql/types";
import type { ExtendedMenuLinkType } from "@/core/types";
import type { LineItemWithAvailabilityStatus } from "@/shared/cart";
import type { NavigationFailure } from "vue-router";
import ForwardCart from "@/shared/cart/components/forward-cart.vue";

defineOptions({
  inheritAttrs: false,
});
const emit = defineEmits<{
  (e: "closeDialog"): void;
  (e: "change:itemQuantity", payload: { itemId: string; quantity: number }): void;
}>();

defineProps<IProps>();
const {
  removeItems,
  changeItemQuantity,
  changeItemQuantityBatched,
  cart,
  cartHasDiscounts,
  availabilityItems,
  itemsInCart,
  lastRemovedLineItem,
  lastAddedItemNumber,
  changeItemQuantityBatchedOverflowed,
  getItemDescription,
  saveCartOnActionURL,
} = useFullCart();
const { user } = useUser();
const { t } = useI18n();
const { currentCurrency } = useCurrency();

interface IProps {
  link?: ExtendedMenuLinkType | undefined;
  count?: number | undefined;
  isActive: boolean;
  href: string;
  navigate: (e?: MouseEvent) => Promise<void | NavigationFailure>;
}

const router = useRouter();

function handleButtonClick() {
  emit("closeDialog");
}

async function handleCheckoutClick() {
  await router.push({ name: "Cart" });
  handleButtonClick();
}

const handleForwardCartClick = async () => {
  forwardCartLoading.value = true;
  const result = await saveCartOnActionURL(user.value.id);
  if (forwardCartRef.value && result) {
    forwardCartRef.value.showForwardCartDialog(result);
  }
  forwardCartLoading.value = false;
};

const breakpoints = useBreakpoints(breakpointsTailwind);
const isMobile = breakpoints.smaller("md");
const isDisabled = ref(false); // Update this based on your logic
const showItemAddedNotification = ref<boolean>(false);
const forwardCartRef = ref<InstanceType<typeof ForwardCart> | null>(null);
const forwardCartLoading = ref(false);
const showItemRemovedNotification = ref<boolean>(false);
const lineItemQtyMap = ref<Map<string, number> | undefined>(undefined);

const sectionItems = ref<InputCustomerReferenceType[]>([]);

const allowedEnclosuresProductCodes = [
  "EC1515A-CUSTOM",
  "EC1530A-CUSTOM",
  "EC1530B-CUSTOM",
  "EC2030A-CUSTOM",
  "EC2030B-CUSTOM",
  "EC2530C-CUSTOM",
  "EC1515AR-CUSTOM",
  "EC1530AR-CUSTOM",
  "EC1530BR-CUSTOM",
  "EC2030AR-CUSTOM",
  "EC2030BR-CUSTOM",
  "EC2530CR-CUSTOM",
];

const shouldShowComments = (item: LineItemWithAvailabilityStatus): boolean | undefined => {
  return (
    allowedEnclosuresProductCodes.includes(item.lineItem.product?.code || "") &&
    item.lineItem.configurationItemsTL?.some((c: CartConfigurationItemTypeTl) =>
      Boolean(c.customText && c.customText !== ""),
    )
  );
};

const isQtyDisabled = (item: LineItemTypeExtension) => {
  return item.product?.isCalibrationServiceTL || isVariant(item) || !getProductPrice(item).amount;
};

function isVariant(lineItem: LineItemTypeExtension) {
  return !!lineItem.product?.mainProductId;
}

function decreaseQuantity(item: LineItemTypeExtension) {
  if (!changeItemQuantityBatchedOverflowed.value && lineItemQtyMap.value) {
    const currentQty = lineItemQtyMap.value.get(item.id);
    if (currentQty) {
      const newQty = currentQty - 1;
      if (newQty > 0) {
        lineItemQtyMap.value.set(item.id, newQty);
        changeItemQuantityBatched(item.id, newQty);
      }
    }
  }
}

function increaseQuantity(item: LineItemTypeExtension) {
  if (!changeItemQuantityBatchedOverflowed.value && lineItemQtyMap.value) {
    const currentQty = lineItemQtyMap.value.get(item.id);
    if (currentQty) {
      const newQty = currentQty + 1;
      if (newQty > 1) {
        lineItemQtyMap.value.set(item.id, newQty);
        changeItemQuantityBatched(item.id, newQty);
      }
    }
  }
}

const itemAddedNotification = computed<string>(() => {
  if (lastAddedItemNumber.value?.product?.mainProductId) {
    return t("common.messages.minicart_serial_item_addded", {
      item: lastAddedItemNumber.value?.product?.masterVariation?.slug,
      serial: lastAddedItemNumber.value?.sku,
    });
  }

  return t("common.messages.minicart_item_addded", { item: lastAddedItemNumber.value?.sku });
});

const itemRemovedNotification = computed<string>(() => {
  if (lastRemovedLineItem.value && lastRemovedLineItem.value?.product?.mainProductId) {
    return t("common.messages.minicart_serial_item_removed", {
      item: lastRemovedLineItem.value?.product?.masterVariation?.slug,
      serial: lastRemovedLineItem.value?.sku,
    });
  }

  return t("common.messages.minicart_item_removed", { item: lastRemovedLineItem.value?.sku });
});

function getSubtotalLabel() {
  if (currentCurrency.value.code === "CNY") {
    return t("common.labels.subtotal_vat");
  } else {
    return t("common.labels.subtotal");
  }
}

const useCnyExtraTaxPrice = computed(() => currentCurrency.value.code === "CNY" && !!cart.value?.subTotalWithTax);

const hasDiscounts = computed(() => cartHasDiscounts.value);

// This function logic is similar to what exists in cart page, see: cartTotalForCartPage
// For displaing the cart value without discounts, without tax and without shipping -> this translates to subtotal - discount
const cartTotalForCartPage = computed(() => {
  // For cart page, calculate subtotal - discount

  const subtotal = useCnyExtraTaxPrice.value ? cart.value?.subTotalWithTax : cart.value?.subTotal;

  // If there are no discounts, return subtotals
  if (!hasDiscounts.value) {
    return subtotal;
  }

  // If subtotal or discount total are missing, return subtotal
  if (!subtotal || !cart.value?.discountTotal) {
    return subtotal;
  }
  // Instead of creating a new object, let's use a deep copy of the subtotal
  // and just modify the amount
  const result = { ...subtotal };

  const discount = useCnyExtraTaxPrice.value
    ? cart.value?.discountTotalWithTax?.amount
    : cart.value?.discountTotal?.amount;

  result.amount = subtotal.amount - (discount ?? 0);

  return result;
});

watch(lastAddedItemNumber, (newItemAdded) => {
  if (newItemAdded) {
    showItemAddedNotification.value = true;
  }
});

watch(lastRemovedLineItem, (newItemRemoved) => {
  if (newItemRemoved) {
    showItemRemovedNotification.value = true;
  }
});

function closeItemAddedAlert() {
  showItemAddedNotification.value = false;
}

function closeItemRemovedAlert() {
  showItemRemovedNotification.value = false;
}

async function removeCartItem(itemId: string) {
  await removeItems([itemId]);
}

defineExpose({
  sectionItems,
});

watch(availabilityItems, () => {
  lineItemQtyMap.value = new Map(availabilityItems.value?.map((x) => [x.lineItem.id, x.lineItem.quantity]));
});

const DEFAULT_THUMB_SIZE = isMobile.value ? 80 : 100;
const DEFAULT_DETAIL_THUMB_SIZE = isMobile.value ? 80 : 100;
</script>

<style lang="scss">
.price-per-unit,
.price-line-trough {
  span {
    @apply font-normal #{!important};
  }
}

.minicart-dialog {
  > .dialog-content {
    @apply pb-0 #{!important};
  }

  .vc-alert--outline-dark {
    @apply text-sm  #{!important};

    .vc-alert__content {
      @apply font-semibold;
    }
  }
  .cart__item-image img {
    @apply object-contain #{!important};
  }
}
.cart__item-price {
  .price-bold {
    @apply font-bold;
  }
}
</style>
